import ImageComp from 'next/image';
import React, { useEffect, useRef, useState } from 'react';
import Xarrow from 'react-xarrows';
import { useIcon } from '../../Hooks/useIcon';

const DraggablesAndLinks: React.FC<{
  elements: any;
}> = ({ elements }) => {
  const containerRef = useRef(null);
  const [loadedImageUrl, setLoadedImageUrl] = useState<string | null>(null);
  const [imageDimensions, setImageDimensions] = useState<{
    width: number;
    height: number;
  } | null>(null);

  const {
    Blocks,
    Lines,
    Dots,
    imageUrl,
    AnchorsLinesColor,
    ContainerHeight,
    DisplayWidth,
    Align,
  } = elements;

  // Horizontal placement of the whole element on the screen. Applied via
  // text-align on a full-width wrapper + an inline-block container, which is
  // robust regardless of the surrounding cell's layout. Only has a visible
  // effect once the element is narrower than the page (DisplayWidth set).
  const alignValue: 'left' | 'right' | 'center' =
    Align === 'left' ? 'left' : Align === 'right' ? 'right' : 'center';

  useEffect(() => {
    if (!imageUrl) return;

    // Load the image to fetch its dimensions
    const img = new Image();
    img.src = imageUrl;
    img.onload = () => {
      setLoadedImageUrl(imageUrl);
      setImageDimensions({
        width: img.naturalWidth,
        height: img.naturalHeight,
      });
    };
  }, [imageUrl]);

  const TitleIcon = (props: any) => {
    const Icon = useIcon(props);
    return Icon;
  };

  return (
    <div style={{ width: '100%', textAlign: alignValue }}>
      <div
        className="drag-container-outer"
        id="dragContainerOuter"
        ref={containerRef}
        style={{
          position: 'relative',
          display: 'inline-block',
          verticalAlign: 'top',
          // reset so the alignment doesn't cascade into hotspot text
          textAlign: 'left',
          height: imageUrl ? 'auto' : `${ContainerHeight}px`,
          // A saved DisplayWidth pins the image + hotspots to one coordinate
          // space shared with the editor; the wrapper's text-align then places
          // the whole (inline-block) element left/center/right on the screen.
          width: DisplayWidth ? `${DisplayWidth}px` : '100%',
          maxWidth: '100%',
          minWidth: DisplayWidth ? undefined : 300,
          minHeight: 100,
        }}
      >
      {Blocks?.length > 0 &&
        Blocks?.map((block: any) => (
          <div
            data-href={block.docLink}
            id={block.id + 'rp' + imageUrl.split('/').pop()}
            key={block.id + 'rp' + imageUrl.split('/').pop()}
            style={{
              opacity: block.visible ? 1 : 0,
              position: 'absolute',
              left: block.position.x,
              top: block.position.y,
              height: 'auto',
              width: 'auto',
              maxWidth: 300,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexDirection: 'column',
              fontWeight: 700,
              backgroundColor: block.bgColor,
              color: block.textColor,
              border: `3px solid ${block.borderColor}`,
              borderRadius: '12px',
              cursor: 'pointer',
              fontSize: 18,
              padding: '5px 10px',
            }}
            className="draggable-box"
          >
            <div
              className="flex ai-center"
              style={{ justifyContent: 'center' }}
            >
              {!block.hideIcon && (
                <div
                  style={{
                    margin: 5,
                    fontSize: 30,
                    display: 'flex',
                  }}
                  className="icon-container flex ai-center"
                >
                  <TitleIcon
                    style={{ fontSize: 26 }}
                    color={block.iconColor}
                    iconName={block.icon}
                  />
                </div>
              )}
              {block.showTitle && (
                <div className="d-box-title">{block.defaultText}</div>
              )}
            </div>
          </div>
        ))}
      {Dots?.length > 0 &&
        Dots?.map((dot: any) => (
          <div
            id={dot.id + 'rp' + imageUrl.split('/').pop()}
            key={dot.id + 'rp' + imageUrl.split('/').pop()}
            style={{
              opacity: 1,
              position: 'absolute',
              left: dot.position.x,
              top: dot.position.y,
              width: 30,
              height: 30,
              backgroundColor: AnchorsLinesColor,
              borderRadius: '50%',
              cursor: 'pointer',
              padding: 5,
            }}
            className="box"
          />
        ))}
      <div className="lines">
        {Lines?.length > 0 &&
          Lines?.map((line: any) => (
            <Xarrow
              key={line.startElem + '' + line.endElem}
              showHead={false}
              path={line.path || 'straight'}
              color={AnchorsLinesColor}
              start={line.startElem + 'rp' + imageUrl.split('/').pop()}
              end={line.endElem + 'rp' + imageUrl.split('/').pop()}
            />
          ))}
      </div>
      {loadedImageUrl && imageDimensions && (
        <ImageComp
          key={decodeURIComponent(loadedImageUrl)}
          unoptimized
          alt={'draggable'}
          src={decodeURIComponent(loadedImageUrl)}
          width={imageDimensions.width} // Original width
          height={imageDimensions.height} // Original height
          style={{
            display: 'block',
            margin: DisplayWidth ? 0 : '0 auto',
            width: DisplayWidth ? '100%' : undefined,
            maxWidth: '100%',
            height: 'auto',
          }}
        />
      )}
      </div>
    </div>
  );
};

export default DraggablesAndLinks;
